import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { StreetBeatChatProvider } from 'shared/providers/StreetBeatChatProvider';
import { ChatPanel } from './ChatPanel';
import assistantNameStore from 'shared/store/assistantNameStore';

/**
 * Whether the value moved from one existing identity to a different existing
 * identity (e.g. thread A → thread B, assistant X → assistant Y). Transitions
 * involving `undefined` are NOT swaps: `undefined → "abc"` happens right after
 * `createThreadAndStart` populates the URL and must NOT remount the provider
 * (that would tear down the just-created chat). `"abc" → undefined` happens
 * on /assistant (new chat) and also keeps the same key.
 */
const isSwap = (prev: string | undefined, curr: string | undefined): boolean =>
  prev !== undefined && curr !== undefined && prev !== curr;

export const ChatPanelWithProvider: React.FC = () => {
  const params = useParams();
  const currentThreadId = params.threadId;
  const selectedAssistant = assistantNameStore((state) => state.selectedAssistant);

  // Bump a version counter synchronously during render whenever we observe a
  // "swap" of either threadId or assistant. Using a ref + render-time mutation
  // (rather than `useEffect(() => setProviderKey(...))`) is deliberate: the
  // effect-based approach left ONE render where the existing SDK provider saw
  // a NEW `threadId` prop before the key bump remounted it, causing the SDK
  // to start reconnecting to the new thread — which then raced against the
  // post-effect remount's fresh connect. Two parallel websockets opened to
  // the same thread; the loser fired `ws.onerror` and crashed the app
  // (uncaught runtime error on history-item navigation).
  //
  // Ref mutation during render is a sanctioned React pattern when the update
  // is deterministic from inputs (same threadId+assistant → same version).
  const versionRef = useRef<{ version: number; threadId: string | undefined; assistant: string | undefined }>({
    version: 0,
    threadId: currentThreadId,
    assistant: selectedAssistant,
  });

  if (
    isSwap(versionRef.current.threadId, currentThreadId) ||
    isSwap(versionRef.current.assistant, selectedAssistant)
  ) {
    versionRef.current = {
      version: versionRef.current.version + 1,
      threadId: currentThreadId,
      assistant: selectedAssistant,
    };
  } else if (
    versionRef.current.threadId !== currentThreadId ||
    versionRef.current.assistant !== selectedAssistant
  ) {
    // Non-swap transition (undefined ↔ defined). Track the latest seen
    // values without bumping the version so the existing provider keeps
    // running and the SDK handles the threadId prop change internally.
    versionRef.current = {
      version: versionRef.current.version,
      threadId: currentThreadId,
      assistant: selectedAssistant,
    };
  }

  const providerKey = versionRef.current.version;

  return (
    <StreetBeatChatProvider key={providerKey} threadId={currentThreadId ?? null}>
      <ChatPanel />
    </StreetBeatChatProvider>
  );
};
